import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useEffect, useState } from "react";
import { POST } from "../../service/CRUDService";
import Toast from "../../common/Toast/Toast";
import Dropdown from "../../common/Dropdown/Dropdown";
import { Copy, Download, Eye, EyeOff } from "lucide-react";
import EntityConstants from "../../configs/EntityConstants";
import warningIcon from "../../resources/icons/warning-triangle.svg";
import downloadCSV from "../../service/downloadCSV";

export default function CreateOrganisationUserModal({
  isOpen,
  onClose,
  onUserCreated,
  tenantCode,
  orgCode,
}) {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [userType, setUserType] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    userType: "",
  });
  const [errorMessage, setErrorMessage] = useState("");
  const [successData, setSuccessData] = useState(null);

  const maxCharsName = 50;
  const maxCharsEmail = 100;
  const maxCharsPhone = 15;

  const userTypeOptions = [
    { name: "Select User Type", value: "" },
    { name: "Admin", value: "Admin" },
    { name: "Issuer", value: "Issuer" },
    { name: "Approver", value: "Approver" },
  ];

  const resetForm = () => {
    setFirstName("");
    setLastName("");
    setEmail("");
    setPhone("");
    setUserType("");
    setShowPassword(false);
    setErrors({
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      userType: "",
    });
    setErrorMessage("");
    setSuccessData(null);
  };

  useEffect(() => {
    if (!isOpen) {
      resetForm();
    }
  }, [isOpen]);

  const validateForm = () => {
    let valid = true;
    const newErrors = {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      userType: "",
    };

    if (!firstName.trim()) {
      newErrors.firstName = "First name is required";
      valid = false;
    } else if (firstName.length > maxCharsName) {
      newErrors.firstName = `First name must be ${maxCharsName} characters or less`;
      valid = false;
    }

    if (!lastName.trim()) {
      newErrors.lastName = "Last name is required";
      valid = false;
    } else if (lastName.length > maxCharsName) {
      newErrors.lastName = `Last name must be ${maxCharsName} characters or less`;
      valid = false;
    }

    if (!email.trim()) {
      newErrors.email = "Email is required";
      valid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = "Please enter a valid email address";
      valid = false;
    } else if (email.length > maxCharsEmail) {
      newErrors.email = `Email must be ${maxCharsEmail} characters or less`;
      valid = false;
    }

    if (phone.trim() && phone.length > maxCharsPhone) {
      newErrors.phone = `Phone number must be ${maxCharsPhone} characters or less`;
      valid = false;
    }

    if (!userType.trim()) {
      newErrors.userType = "User type is required";
      valid = false;
    }

    setErrors(newErrors);
    return valid;
  };

  const handleSubmit = () => {
    if (!validateForm()) return;

    setIsLoading(true);
    setErrorMessage("");
    setSuccessData(null);

    const payload = {
      email: email.trim(),
      first_name: firstName.trim(),
      last_name: lastName.trim(),
      user_type: userType,
      tenant_code: tenantCode,
      org_code: orgCode,
    };

    if (phone.trim()) {
      payload.phone = phone.trim();
    }

    POST(EntityConstants.CREATE_ORGANISATION_USERS, payload)
      .then((response) => response.json())
      .then((data) => {
        if (data.success) {
          Toast({
            type: "success",
            title: "Success",
            message: data.message || "User created successfully",
          });
          setSuccessData(data.data);
          onUserCreated();
        } else {
          setErrorMessage(data.message || "Failed to create user");
          Toast({
            type: "error",
            title: "Error",
            message: data.message || "Failed to create user",
          });
        }
      })
      .catch((error) => {
        console.error("Create organisation user error:", error);
        Toast({
          type: "error",
          title: "Error",
          message: "Failed to create user. Please try again.",
        });
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const copyToClipboard = (text) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        Toast({
          type: "success",
          title: "Copied!",
          message: "Copied to clipboard",
        });
      })
      .catch(() => {
        Toast({
          type: "error",
          title: "Error",
          message: "Failed to copy to clipboard",
        });
      });
  };

  const getSelectedUserType = () => {
    return userTypeOptions.find((option) => option.value === userType) || null;
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/60 backdrop-blur-md" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="max-w-xl transform overflow-hidden rounded-2xl bg-cfSecondary p-6 text-left align-middle shadow-xl transition-all w-full relative">
                <button
                  type="button"
                  className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 focus:outline-none"
                  onClick={handleClose}
                >
                  <span className="sr-only">Close</span>
                  &#10005;
                </button>

                <div className="">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-semibold text-cfHeadingText"
                  >
                    {successData
                      ? "User Created Successfully"
                      : "Create New Organisation User"}
                  </Dialog.Title>
                  <p className="text-sm text-cfGlobalText mt-1">
                    {successData
                      ? "User details have been created successfully. Please save the temporary password."
                      : `Create a new user for organisation: ${orgCode}`}
                  </p>

                  {!successData ? (
                    <div className="space-y-4 mt-6">
                      <div className="flex w-full gap-2">
                        <div className="w-[50%]">
                          <label
                            htmlFor="first_name"
                            className="block mb-2 text-sm font-medium text-cfHeadingText"
                          >
                            First Name <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            className={`bg-cfTextInputBorder border ${
                              errors.firstName
                                ? "border-cfIssuedCredentialTemplateCardStatusFailedBG"
                                : "border-cfIssuedScreenSearchBorder"
                            } text-cfGlobalText text-sm rounded-lg focus:ring-cfButton focus:border-cfButton block p-2.5 w-full`}
                            placeholder="e.g. John"
                            value={firstName}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value.length <= maxCharsName) {
                                setFirstName(value);
                                setErrors({ ...errors, firstName: "" });
                              }
                            }}
                          />
                          <div className="flex justify-between items-center mt-1">
                            {errors.firstName && (
                              <p className="text-sm text-cfIssuedCredentialTemplateCardStatusFailedBG">
                                {errors.firstName}
                              </p>
                            )}
                            <div className="text-right text-sm text-cfViewAllTagText ml-auto">
                              <span>
                                {maxCharsName - firstName.length} characters
                                left
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="w-[50%]">
                          <label
                            htmlFor="last_name"
                            className="block mb-2 text-sm font-medium text-cfHeadingText"
                          >
                            Last Name <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            className={`bg-cfTextInputBorder border ${
                              errors.lastName
                                ? "border-cfIssuedCredentialTemplateCardStatusFailedBG"
                                : "border-cfIssuedScreenSearchBorder"
                            } text-cfGlobalText text-sm rounded-lg focus:ring-cfButton focus:border-cfButton block p-2.5 w-full`}
                            placeholder="e.g. Doe"
                            value={lastName}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value.length <= maxCharsName) {
                                setLastName(value);
                                setErrors({ ...errors, lastName: "" });
                              }
                            }}
                          />
                          <div className="flex justify-between items-center mt-1">
                            {errors.lastName && (
                              <p className="text-sm text-cfIssuedCredentialTemplateCardStatusFailedBG">
                                {errors.lastName}
                              </p>
                            )}
                            <div className="text-right text-sm text-cfViewAllTagText ml-auto">
                              <span>
                                {maxCharsName - lastName.length} characters left
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div>
                        <label
                          htmlFor="user_type"
                          className="block mb-2 text-sm font-medium text-cfHeadingText"
                        >
                          User Type <span className="text-red-500">*</span>
                        </label>
                        <Dropdown
                          options={userTypeOptions}
                          selected={getSelectedUserType()}
                          onSelect={(value) => {
                            setUserType(value);
                            if (value && value.trim()) {
                              setErrors((prev) => ({ ...prev, userType: "" }));
                            }
                          }}
                          buttonClass="w-full justify-between border-cfIssuedScreenSearchBorder hover:border-cfButton focus:border-cfButton"
                          menuClass="z-50 w-full"
                        />
                        {errors.userType && (
                          <p className="mt-1 text-sm text-cfIssuedCredentialTemplateCardStatusFailedBG">
                            {errors.userType}
                          </p>
                        )}
                      </div>
                      {/* Email */}
                      <div>
                        <label
                          htmlFor="email"
                          className="block mb-2 text-sm font-medium text-cfHeadingText"
                        >
                          Email <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="email"
                          className={`bg-cfTextInputBorder border ${
                            errors.email
                              ? "border-cfIssuedCredentialTemplateCardStatusFailedBG"
                              : "border-cfIssuedScreenSearchBorder"
                          } text-cfGlobalText text-sm rounded-lg focus:ring-cfButton focus:border-cfButton block p-2.5 w-full`}
                          placeholder="e.g. john.doe@example.com"
                          value={email}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value.length <= maxCharsEmail) {
                              setEmail(value);
                              setErrors({ ...errors, email: "" });
                            }
                          }}
                        />
                        <div className="flex justify-between items-center mt-1">
                          {errors.email && (
                            <p className="text-sm text-cfIssuedCredentialTemplateCardStatusFailedBG">
                              {errors.email}
                            </p>
                          )}
                          <div className="text-right text-sm text-cfViewAllTagText ml-auto">
                            <span>
                              {maxCharsEmail - email.length} characters left
                            </span>
                          </div>
                        </div>
                      </div>
                      <div>
                        <label
                          htmlFor="phone"
                          className="block mb-2 text-sm font-medium text-cfHeadingText"
                        >
                          Phone (Optional)
                        </label>
                        <input
                          type="tel"
                          className={`bg-cfTextInputBorder border ${
                            errors.phone
                              ? "border-cfIssuedCredentialTemplateCardStatusFailedBG"
                              : "border-cfIssuedScreenSearchBorder"
                          } text-cfGlobalText text-sm rounded-lg focus:ring-cfButton focus:border-cfButton block p-2.5 w-full`}
                          placeholder="e.g. 9876543210"
                          value={phone}
                          onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, "");
                            if (value.length <= maxCharsPhone) {
                              setPhone(value);
                              setErrors({ ...errors, phone: "" });
                            }
                          }}
                        />
                        <div className="text-right text-sm text-cfViewAllTagText mt-1">
                          <span>
                            {maxCharsPhone - phone.length} characters left
                          </span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4 mt-6">
                      <div className="p-4 bg-green-50 border-none border-green-200 rounded-lg">
                        <p className="text-green-800 text-sm">
                          User has been created successfully. A temporary
                          password has been generated and sent to the
                          user&apos;s email.
                        </p>
                      </div>

                      <div className="space-y-3">
                        <div>
                          <label
                            htmlFor="name"
                            className="block mb-1 text-sm font-medium text-cfHeadingText"
                          >
                            Name
                          </label>
                          <div className="flex items-center">
                            <input
                              type="text"
                              readOnly
                              className="bg-cfTextInputBorder border border-cfIssuedScreenSearchBorder text-cfGlobalText text-sm rounded-lg p-2.5 w-full"
                              value={`${successData.first_name} ${successData.last_name}`}
                            />
                            <button
                              type="button"
                              onClick={() =>
                                copyToClipboard(
                                  `${successData.first_name} ${successData.last_name}`
                                )
                              }
                              className="ml-2 p-2 text-gray-500 hover:text-gray-700 focus:outline-none"
                            >
                              <Copy size={18} />
                            </button>
                          </div>
                        </div>

                        <div>
                          <label
                            htmlFor="email"
                            className="block mb-1 text-sm font-medium text-cfHeadingText"
                          >
                            Email
                          </label>
                          <div className="flex items-center">
                            <input
                              type="email"
                              readOnly
                              className="bg-cfTextInputBorder border border-cfIssuedScreenSearchBorder text-cfGlobalText text-sm rounded-lg p-2.5 w-full"
                              value={successData.email}
                            />
                            <button
                              type="button"
                              onClick={() => copyToClipboard(successData.email)}
                              className="ml-2 p-2 text-gray-500 hover:text-gray-700 focus:outline-none"
                            >
                              <Copy size={18} />
                            </button>
                          </div>
                        </div>

                        <div>
                          <label
                            htmlFor="temp_password"
                            className="block mb-1 text-sm font-medium text-cfHeadingText"
                          >
                            Temporary Password
                          </label>
                          <div className="flex items-center">
                            <div className="relative flex-grow">
                              <input
                                type={showPassword ? "text" : "password"}
                                readOnly
                                className="bg-cfTextInputBorder border border-cfIssuedScreenSearchBorder text-cfGlobalText text-sm rounded-lg p-2.5 w-full pr-10"
                                value={successData.temp_password}
                              />
                              <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                className="absolute focus:outline-none right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                              >
                                {showPassword ? (
                                  <EyeOff size={18} />
                                ) : (
                                  <Eye size={18} />
                                )}
                              </button>
                            </div>
                            <button
                              type="button"
                              onClick={() =>
                                copyToClipboard(successData.temp_password)
                              }
                              className="ml-2 p-2 text-gray-500 hover:text-gray-700 focus:outline-none"
                            >
                              <Copy size={18} />
                            </button>
                            <button
                              type="button"
                              onClick={() =>
                                downloadCSV({
                                  email: successData.email,
                                  temporary_password: successData.temp_password
                                  },
                                  "organisation_user_credentials.csv"
                                )
                              }
                              className="ml-2 p-2 text-gray-500 hover:text-gray-700 focus:outline-none"
                            >
                              <Download size={21} />
                            </button>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-4 p-4 bg-red-50 rounded-lg">
                        <div>
                          <img src={warningIcon} className="h-10 w-10" alt="" />
                        </div>
                        <div className="text-red-800 text-sm">
                          Note: Please save these details now. For security
                          reasons, the user&apos;s temporary password will not
                          be displayed again once this window is closed.
                        </div>
                      </div>
                    </div>
                  )}

                  {errorMessage && (
                    <p className="mt-4 text-sm text-cfIssuedCredentialTemplateCardStatusFailedBG flex items-center">
                      <span className="mr-1 text-base leading-none">*</span>
                      {errorMessage}
                    </p>
                  )}
                </div>

                <div className="flex mt-8 justify-end gap-4">
                  {!successData ? (
                    <>
                      <button
                        type="button"
                        className="px-4 py-2 text-cfHeadingText border hover:bg-cfCardHover transition-all border-cfHeadingText rounded-[8px] focus:outline-none focus:ring-3 focus:ring-offset-2"
                        onClick={handleClose}
                        disabled={isLoading}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="px-4 py-2 bg-cfButton hover:bg-cfPrimaryDark text-cfSecondary font-semibold border transition-all border-cfHeadingText rounded-[8px] focus:outline-none focus:ring-3 focus:ring-offset-2 flex items-center justify-center min-w-[120px]"
                        onClick={handleSubmit}
                        disabled={isLoading}
                      >
                        {isLoading ? (
                          <span className="flex items-center">
                            <svg
                              className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              ></circle>
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                              ></path>
                            </svg>
                            Creating...
                          </span>
                        ) : (
                          "Create User"
                        )}
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      className="px-4 py-2 bg-cfButton hover:bg-cfPrimaryDark text-cfSecondary font-semibold border transition-all border-cfHeadingText rounded-[8px] focus:outline-none focus:ring-3 focus:ring-offset-2"
                      onClick={handleClose}
                    >
                      Done
                    </button>
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
